import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import Collapse from '@material-ui/core/Collapse';
import CircularProgress from '@material-ui/core/CircularProgress';

import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';
import ConfirmationMessage from '../Controls/ConfirmationMessage';
import PolicyRowHeader from './PolicyRowHeader';
import PolicyRowBody from './PolicyRowBody';

import { calcMfaWindow, getMatchingByOptions } from './PoliciesUtils';
import {
    AD_ENTITY_TYPES,
    DIRECTORY_TYPES,
    FEATURE_FLAGS_NAMES,
    LOCATION_TYPE_OPTIONS,
    MFA_WINDOW_TYPES,
    POLICY_ACTION_TYPES,
    POLICY_MODE_TYPES,
    TIME_RESTRICTION_TYPE_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';

import styles from './CssModules/PolicyRow.module.css';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { httpPost } from '../utils';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { getPolicyHeaderFeatureLockDisplayOptions, isS4wlPolicyBodyData } from './utils/utils';

const PolicyRow = ({
    isEditor,
    policyData,
    isSortable,
    changePolicyState,
    isNewPolicies,
    handleDeletePolicy,
    handleSavePolicy,
    policyConfiguration,
    setPolicyConfiguration,
    filteredData,
    onHeaderClicked,
    nextPolicyGroupId,
    setNextPolicyGroupId,
    duplicatePolicy,
    isCreateFromTemplate,
    onEditTemplate,
    templateToEdit = undefined
}) => {
    const { pricingModulesState } = usePricingModulesState();

    // General states
    const [editedPolicyData, setEditedPolicyData] = useState(JSON.parse(JSON.stringify(policyData)));
    const [fullDataLoaded, setFullDataLoaded] = useState(false);
    const [showStateChangeModal, setShowStateChangeModal] = useState(false);
    const [missingFieldsText, setMissingFieldsText] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const [isMfaWindowEveryTime, setIsMfaWindowEveryTime] = useState(false);
    const [mfaWindowType, setMfaWindowType] = useState(MFA_WINDOW_TYPES.MINUTES.value);
    const [mfaWindowValueByType, setMfaWindowValueByType] = useState('');
    const geoLocationEnabled = useFeature(FEATURE_FLAGS_NAMES.GeoLocationEnabled);
    const accordingToIpFF = useFeature(FEATURE_FLAGS_NAMES.AccordingToIp)
    const accordingToIpFFEnabled = isFeatureActive(accordingToIpFF);

    const isCreateFromLogs = isNaN(Number(policyData.id));
    const isCreateFromLogsOrTemplate = isCreateFromLogs || isCreateFromTemplate;

    useEffect(() => {
        const onUnload = (event) => {
            if (isDirty) {
                event.returnValue = 'You have some unsaved changes';
                return 'You have some unsaved changes';
            }
        };
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [isDirty]);

    useEffect(() => {
        if (policyData.isLoaded && !fullDataLoaded) {
            const [newMfaWindowType, newMfaWindowValueByType] = calcMfaWindow(policyData.mfa_window);
            setMfaWindowType(newMfaWindowType);
            setMfaWindowValueByType(newMfaWindowValueByType);
            setIsMfaWindowEveryTime(policyData.mfa_window !== undefined && policyData.mfa_window.toString() === '0');
            setEditedPolicyData(JSON.parse(JSON.stringify(policyData)));
            setFullDataLoaded(true);
        }
    }, [policyData, fullDataLoaded]);

    const fetchUsersSumByEntities = async (entities) => {
        const res = await httpPost(`/policy/entitiesUsersSum`, { entities: entities });
        setEditedPolicyData((editedPolicyData) => ({ ...editedPolicyData, users_sum: res.data.usersSum }));
    };

    const onFieldChanged = (fieldName, newFieldData, options = undefined) => {
        const { shouldSetIsDirty = true } = options ?? {};

        setEditedPolicyData((editedPolicyData) => ({ ...editedPolicyData, [fieldName]: newFieldData }));
        if (fieldName === 'entities') {
            fetchUsersSumByEntities(newFieldData);
        }

        if (shouldSetIsDirty) setIsDirty(true);
    };

    const getValidationErrorText = (missingFieldsList, specialValidations) => {
        const hasMissingFields = missingFieldsList.length > 0;
        const lastMissingField = hasMissingFields ? (
            <span className={styles.missingFieldName}>{missingFieldsList.splice(missingFieldsList.length - 1, 1)}</span>
        ) : (
            ''
        );
        const missingFieldsSpans = missingFieldsList.map((missingField, index) => {
            if (index === missingFieldsList.length - 1) {
                return <span className={styles.missingFieldName}>{missingField}</span>;
            }
            return <span className={styles.missingFieldName}>{missingField}, </span>;
        });

        const specialValidationsObj = specialValidations.map((missingField, index) => {
            return (
                <div key={`errorValidation_${index}`}>
                    <br /> {missingField}
                </div>
            );
        });
        return (
            <div>
                Cannot save policy. {hasMissingFields ? 'Missing ' : ''}
                {missingFieldsSpans} {missingFieldsList.length > 0 ? 'and ' : ' '}
                {lastMissingField}
                {hasMissingFields ? ' field' : ''}
                {missingFieldsList.length === 0 ? '' : 's'}
                {hasMissingFields ? '.' : ''} {specialValidationsObj}
            </div>
        );
    };

    const validatePolicyData = (selectAllApplications) => {
        const missingFields = [];
        const specialValidations = [];
        if (!editedPolicyData.name) {
            missingFields.push('Policy Name');
        }

        if (
            !selectAllApplications &&
            (editedPolicyData.relying_parties_enabled || editedPolicyData.ping_fed_enabled) &&
            (!editedPolicyData.relying_parties || editedPolicyData.relying_parties.length === 0)
        ) {
            missingFields.push('Destination');
        }

        if (
            editedPolicyData.risk_enabled &&
            !editedPolicyData.risk_by_level &&
            (!editedPolicyData.risk_indicators || editedPolicyData.risk_indicators.length === 0)
        ) {
            missingFields.push('Risk Indicators');
        }

        if (editedPolicyData.action_type === POLICY_ACTION_TYPES.MFA.value) {
            if (
                (editedPolicyData.radius_enabled ||
                    editedPolicyData.ldap_enabled ||
                    editedPolicyData.oidc_enabled ||
                    editedPolicyData.desktop_enabled) &&
                (!editedPolicyData.display_name || editedPolicyData.display_name === '')
            ) {
                missingFields.push('MFA Prompt Display Name');
            }
            if (!editedPolicyData.allowed_token_types || editedPolicyData.allowed_token_types.length === 0) {
                missingFields.push('Tokens');
            }
            if (!isMfaWindowEveryTime && (parseInt(mfaWindowValueByType) === 0 || mfaWindowValueByType === '')) {
                missingFields.push('valid MFA prompt interval');
            }
            if (editedPolicyData.allowed_token_types && editedPolicyData.desktop_enabled) {
                if (editedPolicyData.mode_type === POLICY_MODE_TYPES.OFFLINE.value) {
                    if (editedPolicyData.allowed_token_types.length === 0) {
                        specialValidations.push('Offline policies Must contain at least one token');
                    }
                }
            }
        }
        if (editedPolicyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value) {
            if (!editedPolicyData.sso_vendor_id) {
                missingFields.push('IdP');
            }
            if (!editedPolicyData.sso_app_id) {
                missingFields.push('Enterprise App');
            }
        }

        if (editedPolicyData.time_restriction_type !== TIME_RESTRICTION_TYPE_OPTIONS.ALWAYS.value) {
            if (editedPolicyData.time_restriction_data.days.length === 0) {
                missingFields.push('Days');
            }
            if (!editedPolicyData.time_restriction_data.to_time) {
                missingFields.push('Start time');
            }
            if (!editedPolicyData.time_restriction_data.from_time) {
                missingFields.push('End time');
            }
            if (Object.keys(editedPolicyData.time_restriction_data.timezone).length === 0) {
                missingFields.push('Timezone');
            }
        }
        if (isFeatureActive(geoLocationEnabled)) {
            if (editedPolicyData.location_type === LOCATION_TYPE_OPTIONS.INCLUDE_LOCATIONS.value) {
                if (!editedPolicyData.locations || editedPolicyData.locations.length === 0) {
                    missingFields.push('Locations');
                }
            }
            if (editedPolicyData.location_type === LOCATION_TYPE_OPTIONS.EXCLUDE_LOCATIONS.value) {
                if (!editedPolicyData.excluded_locations || editedPolicyData.excluded_locations.length === 0) {
                    missingFields.push('Locations');
                }
            }
        }

        let dcInSourceError = false;
        let dcInDestinationError = false;

        if (editedPolicyData.hosts && editedPolicyData.kerberos_enabled) {
            for (const index in editedPolicyData.hosts) {
                const host = editedPolicyData.hosts[index];
                if (!host.excluded && policyConfiguration.DomainControllers.some((dc) => dc.guid === host.guid)) {
                    dcInSourceError = true;
                }
            }
        }

        if (editedPolicyData.servers && editedPolicyData.ntlm_enabled) {
            for (const index in editedPolicyData.servers) {
                const server = editedPolicyData.servers[index];
                if (!server.excluded && policyConfiguration.DomainControllers.some((dc) => dc.guid === server.guid)) {
                    dcInDestinationError = true;
                }
            }
        }

        if (editedPolicyData.servers_ips && editedPolicyData.ldap_enabled) {
            for (const index in editedPolicyData.servers_ips) {
                const servers_ip = editedPolicyData.servers_ips[index];
                if (policyConfiguration.DomainControllers.some((dc) => dc.ipAddress === servers_ip.address)) {
                    dcInDestinationError = true;
                }
            }
        }

        if (dcInSourceError) {
            specialValidations.push('This policy cannot be enforced for this protocol, with a DC as Source.');
        }
        if (dcInDestinationError) {
            specialValidations.push('This policy cannot be enforced for this protocol, with a DC as Destination.');
        }

        if (missingFields.length > 0 || specialValidations.length > 0) {
            setMissingFieldsText(getValidationErrorText(missingFields, specialValidations));
            return false;
        }
        return true;
    };

    const setStateAndSavePolicy = async (newPolicyState) => {
        const policyToSave = { ...editedPolicyData };
        if (newPolicyState !== undefined) {
            policyToSave.state = newPolicyState;
        }
        const originalId = editedPolicyData.id;

        const matchingByOptions = getMatchingByOptions(policyToSave, accordingToIpFFEnabled);
        const matchingByOptionsValues = matchingByOptions.map((option) => option.value);
        const policiesMatchingBy = (policyToSave.matching_by || []).filter((val) =>
            matchingByOptionsValues.includes(val)
        );
        policyToSave.matching_by = policiesMatchingBy;

        if (isS4wlPolicyBodyData(policyToSave)) {
            const shouldHaveLocalAccountAuthType =
                policyToSave.match_all_local_accounts ||
                !!policyToSave.entities?.some(
                    (entity) => entity.type === AD_ENTITY_TYPES.LOCAL_ACCOUNT && !entity.excluded
                );
            const hasLocalAccountAccountType = policyToSave.account_types.includes(DIRECTORY_TYPES.local.key);

            if (shouldHaveLocalAccountAuthType && !hasLocalAccountAccountType) {
                policyToSave.account_types = [...policyToSave.account_types, DIRECTORY_TYPES.local.key];
            } else if (!shouldHaveLocalAccountAuthType) {
                policyToSave.account_types = policyToSave.account_types?.filter(
                    (type) => type !== DIRECTORY_TYPES.local.key
                );
            }

            if (policyToSave.match_all_local_accounts && policyToSave.entities) {
                policyToSave.entities = policyToSave.entities.filter(
                    (entity) => entity.type !== AD_ENTITY_TYPES.LOCAL_ACCOUNT || entity.excluded
                );
            }
        } else {
            policyToSave.match_all_local_accounts = false;
            if (policyToSave.entities) {
                policyToSave.entities = policyToSave.entities?.filter(
                    (entity) => entity.type !== AD_ENTITY_TYPES.LOCAL_ACCOUNT
                );
            }
            if (policyToSave.account_types) {
                policyToSave.account_types = policyToSave.account_types?.filter(
                    (type) => type !== DIRECTORY_TYPES.local.key
                );
            }
        }

        const { result: saveSucceeded, errorDesc } = await handleSavePolicy(policyToSave);
        if (!saveSucceeded) {
            return { data: errorDesc || 'Failed to save policy.', error: true };
        } else {
            if (originalId < 0 || isNaN(originalId) || policyData.action_type !== policyToSave.action_type) {
                return { data: '', error: false };
            }
            setIsDirty(false);
            setEditedPolicyData(policyToSave);
            return { data: 'Saved!', error: false };
        }
    };

    const handleDiscardChanges = () => {
        setEditedPolicyData(JSON.parse(JSON.stringify(policyData)));
        const [newMfaWindowType, newMfaWindowValueByType] = calcMfaWindow(policyData.mfa_window);
        setMfaWindowType(newMfaWindowType);
        setMfaWindowValueByType(newMfaWindowValueByType);
        setIsMfaWindowEveryTime(policyData.mfa_window !== undefined && policyData.mfa_window.toString() === '0');

        setIsDirty(false);
        return policyData;
    };

    const onChangePolicyStateApprove = async (callback) => {
        const results = await changePolicyState(policyData.id);

        if (results) {
            callback(results, false);
            setShowStateChangeModal(false);
        } else {
            callback(null, 'Failed to change state');
        }
    };

    const onDuplicatePolicyClicked = () => {
        return duplicatePolicy(editedPolicyData);
    };

    useEffect(() => {
        if (policyData.entities && isCreateFromLogsOrTemplate) {
            fetchUsersSumByEntities(policyData.entities);
        }
    }, [policyData.entities, isCreateFromLogsOrTemplate]);

    return (
        <>
            <div className={styles.policyRowPadding} id={policyData.id}></div>
            <div className={styles.policyRow}>
                <PolicyRowHeader
                    policyTitleData={policyData}
                    editedPolicyData={editedPolicyData}
                    isExpanded={policyData.isExpanded}
                    onHeaderClicked={onHeaderClicked}
                    isNewPolicies={isNewPolicies}
                    isEditor={isEditor}
                    isSortable={isSortable}
                    setShowStateChangeModal={setShowStateChangeModal}
                    filteredData={filteredData}
                    isDirty={isDirty}
                    policyConfiguration={policyConfiguration}
                    onDuplicatePolicyClicked={onDuplicatePolicyClicked}
                    handleDeletePolicy={handleDeletePolicy}
                    shouldDisplayActions={!templateToEdit}
                    featureLockDisplayOptions={getPolicyHeaderFeatureLockDisplayOptions({
                        policyBodyData: policyData,
                        hiddenRiskIndicators: policyConfiguration.hiddenRiskIndicators,
                        pricingModules: pricingModulesState
                    })}
                />
                <Collapse in={policyData.isExpanded}>
                    {!editedPolicyData.isLoaded && (
                        <div className={`${styles.policyBody} ${styles.policyLoadingBody}`}>
                            <CircularProgress color="inherit" />
                        </div>
                    )}
                    {editedPolicyData.isLoaded && (
                        <PolicyRowBody
                            policyBodyData={editedPolicyData}
                            setPolicyBodyData={setEditedPolicyData}
                            validatePolicyData={validatePolicyData}
                            setStateAndSavePolicy={setStateAndSavePolicy}
                            handleDeletePolicy={handleDeletePolicy}
                            handleDiscardChanges={handleDiscardChanges}
                            policyConfiguration={policyConfiguration}
                            setPolicyConfiguration={setPolicyConfiguration}
                            isEditor={isEditor}
                            onFieldChanged={onFieldChanged}
                            isDirty={isDirty}
                            setIsDirty={setIsDirty}
                            isMfaWindowEveryTime={isMfaWindowEveryTime}
                            setIsMfaWindowEveryTime={setIsMfaWindowEveryTime}
                            mfaWindowType={mfaWindowType}
                            setMfaWindowType={setMfaWindowType}
                            mfaWindowValueByType={mfaWindowValueByType}
                            setMfaWindowValueByType={setMfaWindowValueByType}
                            nextPolicyGroupId={nextPolicyGroupId}
                            setNextPolicyGroupId={setNextPolicyGroupId}
                            isCreateFromLogs={isCreateFromLogs}
                            isCreateFromTemplate={isCreateFromTemplate}
                            templateToEdit={templateToEdit}
                            onEditTemplate={onEditTemplate}
                        />
                    )}
                </Collapse>
                {showStateChangeModal && (
                    <ConfirmationMessage
                        title={'Change Status'}
                        body={`Do you want to turn "${policyData.name}" ${policyData.state ? 'OFF' : 'ON'}?`}
                        approveText={'YES'}
                        cancelText={'NO'}
                        OnCancel={() => setShowStateChangeModal(false)}
                        OnApprove={onChangePolicyStateApprove}
                    />
                )}
            </div>
            <Modal
                id="mandatoryUserConfirm"
                dialogClassName="modal-md"
                show={missingFieldsText !== ''}
                onHide={() => {
                    setMissingFieldsText('');
                }}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Save Policy</Modal.Title>
                </Modal.Header>
                <Modal.Body>{missingFieldsText !== '' && <div>{missingFieldsText}</div>}</Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        onClick={() => {
                            setMissingFieldsText('');
                        }}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default PolicyRow;
